import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Box, Typography, Stack } from '@mui/material';
import { BusinessEvolution } from '../../services/analyticsService';

interface BusinessEvolutionChartProps {
  data: BusinessEvolution[];
  title?: string;
}

const PALETTE = ['#000000', '#FFEB5D', '#555555', '#B8A800', '#888888', '#2C2C2C'];

type ChartRow = Record<string, unknown>;

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: unknown; color: string; payload: ChartRow }>;
}) => {
  if (!active || !payload?.length) return null;
  const formFull = (payload[0]?.payload?.form_full as string) ?? '';
  const visible = payload.filter((e) => e.value !== null && e.value !== undefined);
  if (!visible.length) return null;
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #E0E0E0', borderRadius: 2, p: 1.5, minWidth: 240, maxWidth: 340, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={1} color="text.primary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {formFull}
      </Typography>
      {visible.map((entry, i) => {
        const isLast = entry.name.endsWith('_last');
        const bizKey = entry.name.replace(/_last$|_prev$/, '') + '_name';
        const bizName = (entry.payload?.[bizKey] as string) ?? entry.name;
        const val = typeof entry.value === 'number' ? entry.value : null;
        const noPrev = isLast && visible.every((e) => e.name !== entry.name.replace('_last', '_prev'));
        return (
          <Stack key={i} direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.25}>
            <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 0.25, flexShrink: 0, bgcolor: entry.color, opacity: isLast ? 1 : 0.38, mt: '3px' }} />
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', flex: 1 }}>
                {bizName} · {isLast ? 'Última' : 'Anterior'}{noPrev ? ' ★' : ''}
              </Typography>
            </Stack>
            <Typography variant="caption" fontWeight={600} color={val === null ? 'text.disabled' : 'text.primary'} sx={{ flexShrink: 0 }}>
              {val !== null ? `${val.toFixed(1)}%` : '—'}
            </Typography>
          </Stack>
        );
      })}
      {visible.some((e) => e.name.endsWith('_last') && !visible.find((p) => p.name === e.name.replace('_last', '_prev'))) && (
        <Typography variant="caption" color="text.disabled" display="block" mt={0.75}>
          ★ primera auditoría de este tipo
        </Typography>
      )}
    </Box>
  );
};

const BusinessEvolutionChart: React.FC<BusinessEvolutionChartProps> = ({
  data,
  title = 'Desempeño por Formulario y Negocio',
}) => {
  const { chartData, businesses, yWidth } = useMemo(() => {
    if (!data || data.length === 0)
      return { chartData: [] as ChartRow[], businesses: [] as { id: number; name: string }[], yWidth: 140 };

    const bizMap = new Map<number, string>();
    const formMap = new Map<number, string>();
    for (const row of data) {
      bizMap.set(row.business_id, row.business_name);
      formMap.set(row.form_id, row.form_name);
    }
    const businesses = Array.from(bizMap.entries()).map(([id, name]) => ({ id, name }));
    const forms = Array.from(formMap.entries()).map(([id, name]) => ({ id, name }));

    // índice: `${form_id}_${business_id}` → { last, prev }
    const index = new Map<string, { last: number; prev: number | null }>();
    for (const row of data) {
      index.set(`${row.form_id}_${row.business_id}`, {
        last: row.last_compliance,
        prev: row.prev_compliance,
      });
    }

    const chartData: ChartRow[] = forms.map(({ id: fid, name: fname }) => {
      const row: ChartRow = { form_name: truncate(fname, 32), form_full: fname };
      for (const { id: bid, name: bname } of businesses) {
        const vals = index.get(`${fid}_${bid}`);
        row[`b${bid}_last`] = vals?.last ?? null;
        row[`b${bid}_prev`] = vals?.prev ?? null;
        row[`b${bid}_name`] = bname;
      }
      return row;
    });

    // Calcular ancho del eje Y según el nombre de formulario más largo
    const maxNameLen = Math.max(...forms.map((f) => f.name.length));
    const yWidth = Math.min(Math.max(maxNameLen * 6.5, 120), 220);

    return { chartData, businesses, yWidth };
  }, [data]);

  if (!data || data.length === 0 || chartData.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, gap: 1 }}>
        <Typography variant="body1" color="text.secondary" fontWeight={500}>Sin datos de desempeño</Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={340}>
          Aún no hay auditorías completadas para mostrar el desempeño por formulario y negocio.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {title && (
        <Typography variant="subtitle1" fontWeight={700} mb={1} color="text.primary">
          {title}
        </Typography>
      )}
      <Typography variant="caption" color="text.disabled" display="block" mb={1.5}>
        color sólido = última auditoría  ·  tono apagado = auditoría anterior  ·  ★ = primera de este tipo
      </Typography>

      {/* Leyenda custom: garantiza color = negocio sin depender de recharts */}
      <Stack direction="row" flexWrap="wrap" gap={2} mb={2}>
        {businesses.map((biz, i) => (
          <Stack key={biz.id} direction="row" alignItems="center" gap={0.75}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">{biz.name}</Typography>
          </Stack>
        ))}
      </Stack>

      <ResponsiveContainer width="100%" height={Math.max(chartData.length * (businesses.length * 22 + 28), 200)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
          barCategoryGap="30%"
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 11, fill: '#555' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            type="category"
            dataKey="form_name"
            width={yWidth}
            tick={{ fontSize: 11, fill: '#333' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip content={<CustomTooltip />} wrapperStyle={{ maxWidth: 340, zIndex: 1000 }} />
          {businesses.map((biz, i) => {
            const color = PALETTE[i % PALETTE.length];
            return (
              <React.Fragment key={biz.id}>
                {/* Anterior: mismo color, opaco */}
                <Bar dataKey={`b${biz.id}_prev`} fill={color} radius={[0, 3, 3, 0]} maxBarSize={14} name={`b${biz.id}_prev`}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={color} fillOpacity={entry[`b${biz.id}_prev`] === null ? 0 : 0.32} />
                  ))}
                </Bar>
                {/* Última: color sólido */}
                <Bar dataKey={`b${biz.id}_last`} fill={color} radius={[0, 3, 3, 0]} maxBarSize={14} name={`b${biz.id}_last`}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={color} fillOpacity={entry[`b${biz.id}_last`] === null ? 0 : 1} />
                  ))}
                </Bar>
              </React.Fragment>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

function truncate(str: string, maxLen: number): string {
  if (!str) return '';
  return str.length > maxLen ? str.slice(0, maxLen - 1) + '…' : str;
}

export default BusinessEvolutionChart;
